<template>
  <div
    class="modern-featured-card"
    :style="cardStyle"
  >
    <div class="card-body">
      <div class="text-content">
        <h3 class="card-title">{{ title }}</h3>
        <p class="card-desc">{{ body }}</p>
        <v-btn
          min-width="110"
          height="36"
          elevation="0"
          @click="$emit('click'), $emit('action')"
          :color="ConfigElementsColor"
          class="action-btn text-none font-weight-medium rounded-pill mt-4"
        >
          {{ btnTxt }}
        </v-btn>
      </div>
      <div class="image-wrapper" v-if="imgUrl">
        <img :src="imgUrl" alt="Featured Image" class="featured-img">
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'

const props = defineProps<{
  title: string
  body: string
  btnTxt: string
  imgUrl?: string
  color: string
}>()

defineEmits<{
  (e: 'click'): void
  (e: 'action'): void
}>()

const store = useGlobalsStore()
const { ConfigElementsColor } = storeToRefs(store)

const cardStyle = computed(() => {
  // Use the passed color as a background but give it a modern gradient look
  return {
    background: `linear-gradient(135deg, ${props.color} 0%, rgba(255,255,255,0.7) 100%)`,
    borderColor: props.color
  }
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');

.modern-featured-card {
  font-family: 'Inter', sans-serif;
  border-radius: 20px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 20px;
  height: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 180px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);

    .featured-img {
      transform: scale(1.08) rotate(2deg);
    }
  }
}

.card-body {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  height: 100%;
}

.text-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100%;
}

.card-title {
  font-family: 'Outfit', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
  margin: 0 0 6px 0;
}

.card-desc {
  font-size: 13px;
  line-height: 1.4;
  color: #4b5563;
  margin: 0;
  flex-grow: 1;
}

.action-btn {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  align-self: flex-start;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }
}

.image-wrapper {
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
}

.featured-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 8px 16px rgba(0, 0, 0, 0.08));
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Dark Theme support */
.dark-theme .modern-featured-card,
.dark .modern-featured-card {
  border-color: rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  .card-title {
    color: #ffffff;
  }

  .card-desc {
    color: #9ca3af;
  }
}
</style>
